import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { IFeedPostItem, IPostActivityDog } from "@eagerdog/interfaces";
import { Constants } from "@eagerdog/constants";
import moment from "moment-timezone";

import { helperService } from "src/services/helper.service";
import Button from "src/components/Button/Button";
import Icon from "src/components/Icon/Icon";
import { EventStatusPill, SanctioningClubPill, ShowTypePill } from "src/components/EventPills/EventPills";

import styles from "./EventActivityCard.module.scss";

interface IProps {
    post: IFeedPostItem;
    link: string;
}

const formatFee = (amount: number, currency?: string): string => {
    const code = (currency || "usd").toUpperCase();
    const value = Number.isInteger(amount) ? `${amount}` : amount.toFixed(2);
    return code === "USD" || code === "CAD" ? `$${value} ${code}` : `${value} ${code}`;
};

/**
 * Dogs an event_registered post is about. Posts written before entries were
 * grouped carry one dog on the post itself.
 */
const enteredDogs = (post: IFeedPostItem): IPostActivityDog[] => {
    if (post.activity?.dogs?.length) return post.activity.dogs;
    if (post.dog_id) return [{ dog_id: post.dog_id, call_name: post.dog_call_name, image_url: post.dog_image_url }];
    return [];
};

// Event summary inside an event_published / event_registered feed post:
// date tile, status + sanctioning pills, title, club, address, show types.
// Footer: for a published event, the registration line + "View event"; for
// an entry, the dogs that were entered + "Register again" for the entrant.
const EventActivityCard: React.FC<IProps> = ({ post, link }) => {
    const navigate = useNavigate();
    const a = post.activity;
    if (!a) return null;

    const isEntry = post.post_type === Constants.post_type.event_registered;
    const isEntrant = isEntry && String(post.author_id) === String(helperService.getUser()._id);
    const dogs = isEntry ? enteredDogs(post) : [];

    const live = post.event;
    const now = moment();
    const start = a.start_date ? moment(a.start_date) : undefined;
    const end = a.end_date ? moment(a.end_date) : start;
    const isPast = !!end && end.endOf("day").isBefore(now);

    // Status comes from the live event; fall back to "published" for a card
    // whose event has since been removed.
    let status = live?.status || Constants.event_status.published;
    if (status === Constants.event_status.upcoming && live?.publish_at && moment(live.publish_at).isSameOrBefore(now)) {
        status = Constants.event_status.published;
    }

    const registrationOpen =
        !isPast
        && status === Constants.event_status.published
        && (!live?.registration_end_date || moment(live.registration_end_date).isAfter(now));

    // Entries not open yet: the footer becomes a dated "Entries open" block
    // in the event's own timezone, the same clock the event page shows.
    const opensAt =
        !isPast && status === Constants.event_status.upcoming && live?.publish_at
            ? moment(live.publish_at).tz(live.timezone || moment.tz.guess())
            : undefined;

    let registrationLine: React.ReactNode = null;
    if (isPast) {
        registrationLine = "Event finished";
    } else if (registrationOpen) {
        registrationLine = live?.min_fee != null
            ? <>Registration available · From <strong>{formatFee(live.min_fee, live.currency)}</strong></>
            : "Registration available";
    } else if (status === Constants.event_status.published) {
        registrationLine = "Registration closed";
    }

    const place = [live?.address_line1, [a.city, a.state].filter(Boolean).join(", ")].filter(Boolean).join(" · ");
    const showTypes = live?.show_types || [];
    const flyer = post.media?.find((m) => m.type === Constants.post_media_type.image)?.url;

    return (
        <div className={`${styles.EventActivityCard} ${flyer ? styles.withFlyer : ""}`}>
            {flyer && (
                <a className={styles.flyer} href={flyer} target="_blank" rel="noreferrer" title="Open flyer">
                    <img src={flyer} alt={`${a.event_title || "Event"} flyer`} />
                </a>
            )}
            <Link to={link} className={styles.main}>
                {start && (
                    <div className={styles.dateTile}>
                        <span className={styles.month}>{start.format("MMM")}</span>
                        <span className={styles.day}>{start.format("D")}</span>
                        <span className={styles.year}>{start.format("YYYY")}</span>
                    </div>
                )}
                <div className={styles.details}>
                    <div className={styles.pills}>
                        {a.sanctioning_club && <SanctioningClubPill>{a.sanctioning_club}</SanctioningClubPill>}
                        <EventStatusPill className={styles.status} status={status} isPastEvent={isPast} />
                    </div>
                    <span className={styles.title}>{a.event_title}</span>
                    {a.club_name && (
                        a.club_id ? (
                            // Inside the event link, so the club name navigates by handler rather than a nested anchor.
                            <span
                                role="link"
                                tabIndex={0}
                                className={`${styles.club} ${styles.clubLink}`}
                                onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate(`/k/${a.club_id}`); }}
                                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); e.stopPropagation(); navigate(`/k/${a.club_id}`); } }}
                            >
                                {a.club_name}
                            </span>
                        ) : (
                            <span className={styles.club}>{a.club_name}</span>
                        )
                    )}
                    {place && <span className={styles.place}>{place}</span>}
                    {showTypes.length > 0 && (
                        <div className={styles.showTypes}>
                            {showTypes.map((show) => <ShowTypePill key={show}>{show}</ShowTypePill>)}
                        </div>
                    )}
                </div>
            </Link>
            {isEntry ? (
                <div className={styles.footer}>
                    <div className={styles.enteredDogs}>
                        {dogs.map((dog) => (
                            <Link
                                key={dog.dog_id}
                                to={`/dogs/${dog.dog_id}`}
                                className={styles.enteredDog}
                                title={dog.call_name}
                                aria-label={dog.call_name}
                            >
                                {dog.image_url
                                    ? <img src={dog.image_url} alt={dog.call_name || ""} />
                                    : <span>{(dog.call_name || "?").charAt(0).toUpperCase()}</span>}
                            </Link>
                        ))}
                    </div>
                    {isEntrant && registrationOpen
                        ? <Button className={styles.viewButton} onClick={() => navigate(`${link}/register`)}>Register again</Button>
                        : <Button className={styles.viewButton} onClick={() => navigate(link)}>View event</Button>}
                </div>
            ) : opensAt ? (
                <div className={`${styles.footer} ${styles.opensFooter}`}>
                    <div className={styles.opens}>
                        <span className={styles.opensIcon} aria-hidden="true">
                            <Icon type="calendar" className={styles.opensIconInner} />
                        </span>
                        <div className={styles.opensText}>
                            <span className={styles.opensLabel}>Entries open</span>
                            <span className={styles.opensWhen}>
                                <span className={styles.opensDate}>{opensAt.format("MMM D, YYYY")}</span>
                                <span className={styles.opensDivider} aria-hidden="true" />
                                <span className={styles.opensClock}>
                                    <span className={styles.opensTime}>{opensAt.format("h:mm A")}</span>
                                    <span className={styles.opensZone}>{opensAt.format("z")}</span>
                                </span>
                            </span>
                        </div>
                    </div>
                    <Button className={styles.viewButton} onClick={() => navigate(link)}>View event</Button>
                </div>
            ) : (
                <div className={styles.footer}>
                    <span className={styles.registration}>{registrationLine}</span>
                    <Button className={styles.viewButton} onClick={() => navigate(link)}>View event</Button>
                </div>
            )}
        </div>
    );
};

export default EventActivityCard;
